import {
  getUserControllerMeQueryKey,
  UserRoleName,
  useUserControllerMe,
  type RefSourceDto,
  type RefUserDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { useResponseData } from '../composables/useResponseData';
import { useAuth } from './authStore';
import { registerLoading } from './loadingStore';

export const useUserStore = defineStore('user', () => {
  const { userId, username, isLoggedIn } = useAuth();

  const email = ref('');
  const firstName = ref<string | undefined>(undefined);
  const lastName = ref<string | undefined>(undefined);
  const displayName = ref<string | undefined>(undefined);
  const abbreviation = ref<string | undefined>(undefined);
  const roles = ref<UserRoleName[]>([]);
  const primarySource = ref<RefSourceDto | undefined>(undefined);

  const { data, refetch: updateUserData, isFetching } = useUserControllerMe({
    query: { queryKey: getUserControllerMeQueryKey(), enabled: isLoggedIn },
  });

  // The store outlives every component, so it registers directly instead of
  // going through `watchLoading` - there is no component unmount to clean up on.
  registerLoading(isFetching);

  const userData = useResponseData(data);

  const isReadonly = computed(
    () => roles.value.includes('READONLY') || roles.value.includes('EXTERNAL_INTEGRATION_READONLY'),
  );
  const isExternal = computed(
    () =>
      roles.value.includes('EXTERNAL_USER') ||
      roles.value.includes('EXTERNAL_INTEGRATION') ||
      roles.value.includes('EXTERNAL_INTEGRATION_READONLY'),
  );
  const isAdmin = computed(() => roles.value.includes('ADMIN'));
  const isOfferReviewer = computed(() => roles.value.includes('OFFER_REVIEW') || isAdmin.value);
  const isHandlingReviewer = computed(
    () => roles.value.includes('HANDLING_REVIEW') || isAdmin.value,
  );
  const isHandlingUser = computed(() => roles.value.includes('ITEM_HANDLING') || isAdmin.value);

  const user = computed<RefUserDto | undefined>(() => {
    if (!userData.value) return undefined;

    return {
      id: userId.value,
      username: username.value,
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      displayName: displayName.value,
      abbreviation: abbreviation.value,
      roles: roles.value,
      primarySource: primarySource.value,
    };
  });

  watch(isLoggedIn, (isLoggedIn) => {
    if (!isLoggedIn) {
      email.value = '';
      firstName.value = undefined;
      lastName.value = undefined;
      abbreviation.value = undefined;
      roles.value = [];
      primarySource.value = undefined;
    }
  });

  watch(userData, (userData) => {
    if (userData) {
      email.value = userData.email;
      firstName.value = userData.firstName;
      lastName.value = userData.lastName;
      displayName.value = userData.displayName;
      abbreviation.value = userData.abbreviation ?? undefined;
      roles.value = userData.roles || [];
      primarySource.value = userData.primarySource;
    }
  });

  return {
    userId,
    user,
    username,
    email,
    firstName,
    lastName,
    displayName,
    abbreviation,
    roles,
    primarySource,
    isReadonly,
    isExternal,
    isOfferReviewer,
    isHandlingReviewer,
    isAdmin,
    isHandlingUser,
    updateUserData,
  };
});

export const useUser = () => usePiniaRefStore(useUserStore());
